import React, { useEffect, useMemo, useState } from 'react';
import { FaPlus, FaTrash, FaSave, FaUndo } from 'react-icons/fa';
import Select from '../../../../components/Select';
import DraftBanner from '../../../../components/DraftBanner';
import { supabase } from '../../../../config/supabaseConfig.ts';
import { toastError, toastSuccess } from '../../../../utils/toast';
import { useDraft } from '../../../../hooks/useDraft';

// Perfil de evaluación del proyecto (Fase 2 · Calidad). Por defecto el proyecto usa el
// perfil de su TIPO DE AGENTE (se administra en "Tipos de agente"); aquí el admin puede
// usar otro perfil o ajustar las etapas solo para este proyecto (insight_settings).

const C = { line: 'rgb(var(--fg) / 0.08)', text: 'rgb(var(--fg))', muted: 'rgb(var(--fg-dim))', faint: 'rgb(var(--fg-faint))', input: 'rgb(var(--raised))' };

const toKey = (label, i) =>
  (label || `etapa_${i + 1}`)
    .toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40) || `etapa_${i + 1}`;

const inputStyle = { backgroundColor: C.input, border: `1px solid ${C.line}`, color: C.text };

const QaProfileConfig = ({ projectId, agentType, onProfileChange }) => {
  const [profiles, setProfiles] = useState([]);
  const [form, setForm] = useState({ profileKey: '', customStages: null }); // profileKey '' = el del tipo de agente
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const draft = useDraft(`qa-profile:${projectId}`, form, setForm, { ready: loaded && !!projectId });

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      const [{ data: list, error }, { data: settings }] = await Promise.all([
        supabase.from('qa_profiles').select('key, name, objective, stages').order('name'),
        supabase.from('insight_settings').select('qa_profile, qa_stages').eq('project_id', projectId).maybeSingle(),
      ]);
      if (error) {
        toastError('No se pudieron cargar los perfiles de evaluación');
        return;
      }
      setProfiles(list || []);
      setForm({ profileKey: settings?.qa_profile || '', customStages: settings?.qa_stages || null });
      setLoaded(true);
    })();
  }, [projectId]);

  const inherited = profiles.find((p) => p.key === agentType?.qa_profile);
  const profile = form.profileKey ? profiles.find((p) => p.key === form.profileKey) : inherited;
  const stages = useMemo(() => form.customStages || profile?.stages || [], [form.customStages, profile]);

  useEffect(() => { onProfileChange?.(profile?.name ?? null); }, [profile, onProfileChange]);

  if (!projectId) {
    return (
      <p className="text-sm" style={{ color: C.muted }}>
        Se usará el perfil del tipo de agente. Después de crear el proyecto puedes cambiarlo aquí si lo necesitas.
      </p>
    );
  }
  if (!loaded) return <p className="text-sm" style={{ color: C.muted }}>Cargando perfiles…</p>;

  const setStage = (i, field, value) =>
    setForm((f) => ({ ...f, customStages: stages.map((s, j) => (j === i ? { ...s, [field]: value } : s)) }));

  const handleSave = async () => {
    let qaStages = null;
    if (form.customStages) {
      if (form.customStages.length < 2 || form.customStages.length > 8) { toastError('El embudo necesita entre 2 y 8 etapas'); return; }
      if (form.customStages.some((s) => !s.label?.trim() || !s.description?.trim())) { toastError('Cada etapa necesita nombre y qué tiene que pasar para llegar a ella'); return; }
      const used = new Set();
      qaStages = form.customStages.map((s, i) => {
        let key = s.key || toKey(s.label, i);
        while (used.has(key)) key = `${key}_${i + 1}`;
        used.add(key);
        return { key, label: s.label.trim(), description: s.description.trim() };
      });
    }
    setSaving(true);
    const { error } = await supabase.from('insight_settings').upsert(
      { project_id: projectId, qa_profile: form.profileKey || null, qa_stages: qaStages, updated_at: new Date().toISOString() },
      { onConflict: 'project_id' },
    );
    setSaving(false);
    if (error) { toastError('No se pudo guardar cómo se mide este agente'); return; }
    const saved = { ...form, customStages: qaStages };
    setForm(saved);
    draft.clear();
    toastSuccess('Guardado. Las conversaciones nuevas se leerán con este perfil.');
  };

  return (
    <div className="space-y-4">
      <DraftBanner restoredAt={draft.restoredAt} onDiscard={draft.discard} />

      <fieldset className="space-y-2">
        <legend className="sr-only">Perfil de evaluación</legend>
        <label className="flex items-start gap-3 rounded-xl p-3 cursor-pointer" style={{ border: `1px solid ${!form.profileKey ? 'rgba(0,194,255,0.4)' : C.line}`, backgroundColor: !form.profileKey ? 'rgba(0,194,255,0.05)' : 'transparent' }}>
          <input
            type="radio" name={`qa-mode-${projectId}`} className="mt-1 accent-[#00C2FF]" checked={!form.profileKey}
            onChange={() => setForm({ profileKey: '', customStages: null })}
          />
          <span>
            <span className="block text-sm font-medium" style={{ color: C.text }}>
              Usar el del tipo de agente{agentType?.name ? ` (${agentType.name})` : ''}
            </span>
            <span className="block text-xs mt-0.5" style={{ color: C.muted }}>
              {inherited ? `${inherited.name}: ${inherited.objective}` : 'Este tipo de agente todavía no tiene perfil; se usará “Conversacional”. Asígnalo en Tipos de agente.'}
            </span>
          </span>
        </label>

        <label className="flex items-start gap-3 rounded-xl p-3 cursor-pointer" style={{ border: `1px solid ${form.profileKey ? 'rgba(0,194,255,0.4)' : C.line}`, backgroundColor: form.profileKey ? 'rgba(0,194,255,0.05)' : 'transparent' }}>
          <input
            type="radio" name={`qa-mode-${projectId}`} className="mt-1 accent-[#00C2FF]" checked={!!form.profileKey}
            onChange={() => setForm({ profileKey: inherited?.key || profiles[0]?.key || '', customStages: null })}
          />
          <span className="flex-1">
            <span className="block text-sm font-medium" style={{ color: C.text }}>Usar otro perfil solo en este proyecto</span>
            <span className="block text-xs mt-0.5" style={{ color: C.muted }}>Para un cliente que no encaja con su tipo de agente.</span>
            {!!form.profileKey && (
              <span className="block mt-3 max-w-md">
                <Select
                  id={`qa_profile_${projectId}`} name="qa_profile" label="Perfil"
                  options={profiles.map((p) => ({ value: p.key, label: p.name }))}
                  value={form.profileKey}
                  onChange={(e) => setForm({ profileKey: e.target.value || '', customStages: null })}
                  isSearchable={false} isClearable={false}
                />
              </span>
            )}
          </span>
        </label>
      </fieldset>

      {profile && (
        <div className="space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-sm font-medium" style={{ color: C.text }}>
              Embudo {form.customStages ? 'ajustado para este proyecto' : `de “${profile.name}”`}
            </p>
            {form.customStages ? (
              <button type="button" className="ml-auto text-xs flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF] rounded" style={{ color: '#00C2FF' }}
                onClick={() => setForm((f) => ({ ...f, customStages: null }))}>
                <FaUndo aria-hidden /> Volver a las etapas del perfil
              </button>
            ) : (
              <button type="button" className="ml-auto text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF] rounded" style={{ color: '#00C2FF' }}
                onClick={() => setForm((f) => ({ ...f, customStages: stages.map((s) => ({ ...s })) }))}>
                Ajustar etapas para este proyecto
              </button>
            )}
          </div>

          <ol className="space-y-2">
            {stages.map((s, i) => (
              <li key={i} className="flex gap-3 items-start">
                <span className="mt-1 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold"
                  style={{ backgroundColor: i === stages.length - 1 ? 'rgba(34,197,94,0.15)' : 'rgb(var(--fg) / 0.06)', color: i === stages.length - 1 ? '#86EFAC' : C.muted }}>
                  {i + 1}
                </span>
                {form.customStages ? (
                  <div className="flex flex-1 flex-col sm:flex-row gap-2">
                    <input className="rounded-lg px-3 py-2 text-sm sm:w-48" maxLength={60} style={inputStyle}
                      value={s.label} placeholder="Nombre de la etapa" aria-label={`Nombre de la etapa ${i + 1}`}
                      onChange={(e) => setStage(i, 'label', e.target.value)} />
                    <input className="rounded-lg px-3 py-2 text-sm flex-1" maxLength={200} style={inputStyle}
                      value={s.description} placeholder="Qué tiene que pasar en la conversación para llegar aquí" aria-label={`Descripción de la etapa ${i + 1}`}
                      onChange={(e) => setStage(i, 'description', e.target.value)} />
                    <button type="button" className="p-2 rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]" style={{ color: '#F87171' }}
                      aria-label={`Quitar etapa ${i + 1}`} onClick={() => setForm((f) => ({ ...f, customStages: stages.filter((_, j) => j !== i) }))}>
                      <FaTrash aria-hidden />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm pt-1" style={{ color: 'rgb(var(--fg))' }}>
                    <span className="font-medium">{s.label}</span>
                    <span style={{ color: C.muted }}>. {s.description}</span>
                  </p>
                )}
              </li>
            ))}
          </ol>

          {form.customStages && (
            <button type="button" className="text-xs flex items-center gap-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF] rounded" style={{ color: '#00C2FF' }}
              disabled={form.customStages.length >= 8}
              onClick={() => setForm((f) => ({ ...f, customStages: [...stages, { key: '', label: '', description: '' }] }))}>
              <FaPlus aria-hidden /> Agregar etapa
            </button>
          )}
          <p className="text-xs" style={{ color: C.faint }}>
            La última etapa (en verde) es el objetivo cumplido. Si cambias el perfil o las etapas, el embudo solo cuenta las conversaciones leídas desde ese cambio.
          </p>
        </div>
      )}

      <button
        type="button" onClick={handleSave} disabled={saving}
        className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#00C2FF]"
        style={{ backgroundColor: '#DC1F26', opacity: saving ? 0.6 : 1 }}
      >
        <FaSave aria-hidden /> {saving ? 'Guardando…' : 'Guardar cómo se mide'}
      </button>
    </div>
  );
};

export default QaProfileConfig;
